import React, { useEffect, useMemo, useState } from "react";
import { DateRangePicker } from "react-date-range";
import { ChevronDown, Filter, Loader2 } from "lucide-react";
import CustomModal from "../common/CustomModal";
import { useAppDispatch, useAppSelector } from "../../store/hook";
import type { RootState } from "../../store/store";
import { MISVoucherTable } from "./MISVoucherTable";
import { fetchPrincipalList } from "../../features/principal/PrincipalSlice";
import { fetchProgramList } from "../../features/program/ProgramSlice";
import { fetchDistributors } from "../../features/distributor/DistributorSlice";
import { MultiSelectDropdown } from "../Dashboard/filters/MultiSelectDropdown";
import type { FilterOption } from "../Dashboard/filters/types";
import { fetchDistributorMIS, fetchPrincipalMIS, type MISFilters } from "../../features/mis/MISSlice";
import { UpcomingExpiriesSection } from "./sections/UpcomingExpiriesSection";
import { VoucherStatsSection } from "./sections/VoucherStatsSection";
import {
  VoucherCreationByDateSection,
  VoucherExpiryByDateSection,
  VoucherUsageByDateSection,
} from "./sections/DailyBreakdownSection";
import { ProgramSummarySection } from "./sections/ProgramSummarySection";
import { DistributorSummarySection } from "./sections/DistributorSummarySection";
import { StatusSummarySection } from "./sections/StatusSummarySection";
import { useMoney } from "../../hooks/useMoney";

const formatDateForApi = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

type DateRangeState = {
  startDate: Date;
  endDate: Date;
  key: "selection";
};


const getDefaultRange = (): DateRangeState[] => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  return [{ startDate: start, endDate: end, key: "selection" }];
};

const AdminMIS: React.FC = () => {
  const { locale } = useMoney();
  const dispatch = useAppDispatch();
  const [dateRange, setDateRange] = useState<DateRangeState[]>(() => getDefaultRange());
  const [dateModalOpen, setDateModalOpen] = useState(false);

  const [selectedPrincipalIds, setSelectedPrincipalIds] = useState<number[]>([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState<number[]>([]);
  const [selectedDistributorIds, setSelectedDistributorIds] = useState<number[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);

  const [showFilters, setShowFilters] = useState(false);

  const [principalDropdownOpen, setPrincipalDropdownOpen] = useState(false);
  const [programDropdownOpen, setProgramDropdownOpen] = useState(false);
  const [distributorDropdownOpen, setDistributorDropdownOpen] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);

  const { principalList } = useAppSelector((s: RootState) => s.principal);
  const { programList } = useAppSelector((s: RootState) => s.program);
  const { distributorList } = useAppSelector((s: RootState) => s.distributor);
  const { principal, distributor, error, principalLoading } = useAppSelector(
    (s: RootState) => s.mis
  );

  useEffect(() => {
    if (!principalList) dispatch(fetchPrincipalList());
    if (!programList) dispatch(fetchProgramList({}));
    if (!distributorList) dispatch(fetchDistributors(null));
  }, [dispatch, principalList, programList, distributorList]);

  const principalOptions = useMemo<FilterOption<number>[]>(
    () =>
      (principalList || []).map((p) => ({
        value: Number(p.principalId),
        label: p.name,
      })),
    [principalList]
  );

  const programOptions = useMemo<FilterOption<number>[]>(
    () =>
      (programList || []).map((p) => ({
        value: Number(p.programId),
        label: p.programName,
      })),
    [programList]
  );

  const distributorOptions = useMemo<FilterOption<number>[]>(
    () =>
      (distributorList || []).map((d) => ({
        value: Number(d.distributorId),
        label: d.name,
      })),
    [distributorList]
  );

  const stateOptions = useMemo<FilterOption<string>[]>(
    () => [
      { value: "ISSUED", label: "Issued" },
      { value: "ACTIVATED", label: "Activated" },
      { value: "USED", label: "Used" },
      { value: "EXHAUSTED", label: "Exhausted" },
      { value: "EXPIRED", label: "Expired" },
      { value: "REVOKED", label: "Revoked" },
      { value: "FAILED", label: "Failed" },
    ],
    []
  );

  const filtersForApi = useMemo<MISFilters>(() => {
    const fromDate = formatDateForApi(dateRange[0].startDate);
    const toDate = formatDateForApi(dateRange[0].endDate);

    const body: MISFilters = { fromDate, toDate };
    if (selectedPrincipalIds.length) body.principalIds = selectedPrincipalIds;
    if (selectedProgramIds.length) body.programIds = selectedProgramIds;
    if (selectedDistributorIds.length) body.distributorIds = selectedDistributorIds;
    if (selectedStates.length) body.states = selectedStates;

    return body;
  }, [
    dateRange,
    selectedPrincipalIds,
    selectedProgramIds,
    selectedDistributorIds,
    selectedStates,
  ]);

  const fetchPrincipalData = (page: number) => {
    dispatch(fetchPrincipalMIS({ filters: filtersForApi, page, limit: 10 }));
  };

  const fetchDistributorData = () => {
    dispatch(fetchDistributorMIS({ filters: filtersForApi, page: 1, limit: 10 }));
  };

  const handleApplyFilters = () => {
    fetchPrincipalData(1);
    fetchDistributorData();
  };


  useEffect(() => {
    handleApplyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRange,
    selectedPrincipalIds,
    selectedProgramIds,
    selectedDistributorIds,
    selectedStates,
  ]);

  const formatHeaderRange = () => {
    const { startDate, endDate } = dateRange[0];
    return `${startDate.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })} - ${endDate.toLocaleDateString(locale, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })}`;
  };

  const onRangeChange = (ranges: any) => {
    setDateRange([ranges.selection]);
  };

  const hasActiveFilters =
    selectedPrincipalIds.length > 0 ||
    selectedProgramIds.length > 0 ||
    selectedDistributorIds.length > 0 ||
    selectedStates.length > 0;

  const clearAllFilters = () => {
    setSelectedPrincipalIds([]);
    setSelectedProgramIds([]);
    setSelectedDistributorIds([]);
    setSelectedStates([]);
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-cardborder bg-white">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 py-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Admin MIS
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Voucher MIS overview for the selected period
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-2 border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-500 shadow-sm transition-colors hover:bg-gray-50"
                onClick={() => setDateModalOpen(true)}
              >
                <span>{formatHeaderRange()}</span>
                <ChevronDown className="h-5 w-5 text-gray-500" />
              </button>
              <button
                type="button"
                className={`inline-flex items-center gap-2 border px-4 py-2 text-sm font-medium transition-colors ${
                  showFilters
                    ? "border-brand bg-brand/10 text-brand shadow-sm"
                    : "border-gray-200 bg-white text-gray-500 shadow-sm hover:bg-gray-50"
                }`}
                onClick={() => setShowFilters((v) => !v)}
              >
                <Filter className="h-4 w-4" />
                Filters
              </button>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="px-4 py-2.5 text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>

          {showFilters && (
            <div className="mt-6 border-t border-cardborder pt-6">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <MultiSelectDropdown<number>
                  label="Principal"
                  allLabel="All Principals"
                  selected={selectedPrincipalIds}
                  options={principalOptions}
                  open={principalDropdownOpen}
                  setOpen={setPrincipalDropdownOpen}
                  onChange={setSelectedPrincipalIds}
                />
                <MultiSelectDropdown<number>
                  label="Program"
                  allLabel="All Programs"
                  selected={selectedProgramIds}
                  options={programOptions}
                  open={programDropdownOpen}
                  setOpen={setProgramDropdownOpen}
                  onChange={setSelectedProgramIds}
                />
                <MultiSelectDropdown<number>
                  label="Distributor"
                  allLabel="All Distributors"
                  selected={selectedDistributorIds}
                  options={distributorOptions}
                  open={distributorDropdownOpen}
                  setOpen={setDistributorDropdownOpen}
                  onChange={setSelectedDistributorIds}
                />
                <MultiSelectDropdown<string>
                  label="Status"
                  allLabel="All States"
                  selected={selectedStates}
                  options={stateOptions}
                  open={stateDropdownOpen}
                  setOpen={setStateDropdownOpen}
                  onChange={setSelectedStates}
                />
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8 pb-12 sm:px-8">
        {error && (
          <div className="mb-6 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {principalLoading && (
          <div className="mb-6 flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin text-brand" />
            Loading MIS data...
          </div>
        )}

        <div className="flex w-full max-w-full flex-col gap-12">
          {principal.statusSummary && (
            <section className="flex w-full flex-col">
              <h2 className="mb-4 border-b border-gray-100 pb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Status Summary
              </h2>
              <StatusSummarySection
                title="Voucher Status Summary"
                data={principal.statusSummary}
              />
            </section>
          )}

          {principal.programSummary?.length > 0 && (
            <section className="flex w-full flex-col">
              <h2 className="mb-4 border-b border-gray-100 pb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Program Voucher Summary
              </h2>
              <ProgramSummarySection data={principal.programSummary} />
            </section>
          )}

          {principal.creationByDate?.length > 0 && (
            <section className="flex w-full flex-col">
              <h2 className="mb-4 border-b border-gray-100 pb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Voucher Creation by Date
              </h2>
              <VoucherCreationByDateSection data={principal.creationByDate} />
            </section>
          )}

          {principal.usageByDate?.length > 0 && (
            <section className="flex w-full flex-col">
              <h2 className="mb-4 border-b border-gray-100 pb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Voucher Usage by Date
              </h2>
              <VoucherUsageByDateSection data={principal.usageByDate} />
            </section>
          )}

          {(principal.expiryByDate?.length > 0 || principal.upcomingExpiries?.length > 0) && (
            <section className="flex w-full flex-col">
              <h2 className="mb-4 border-b border-gray-100 pb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Expiry
              </h2>
              <div className="overflow-hidden border border-cardborder bg-white shadow-sm">
                {principal.expiryByDate?.length > 0 && (
                  <VoucherExpiryByDateSection
                    data={principal.expiryByDate}
                    embedded
                    embeddedLeading
                  />
                )}
                {principal.upcomingExpiries?.length > 0 && (
                  <UpcomingExpiriesSection
                    data={principal.upcomingExpiries}
                    embedded
                    embeddedLeading={!principal.expiryByDate?.length}
                  />
                )}
              </div>
            </section>
          )}

          {principal.voucherStats?.length > 0 && (
            <section className="flex w-full flex-col">
              <h2 className="mb-4 border-b border-gray-100 pb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Voucher Stats
              </h2>
              <VoucherStatsSection title="Voucher Stats" data={principal.voucherStats} />
            </section>
          )}

          {distributor.distSummary?.length > 0 && (
            <section className="flex w-full flex-col">
              <h2 className="mb-4 border-b border-gray-100 pb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Distributor Summary
              </h2>
              <DistributorSummarySection data={distributor.distSummary} />
            </section>
          )}

          {/* {principal.voucherList && (
            <section className="flex w-full flex-col">
              <h2 className="mb-4 border-b border-gray-100 pb-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
                Voucher List
              </h2>
              <MISVoucherTable
                title="Voucher List"
                data={principal.voucherList}
                showDistributorColumn={false}
                onPageChange={fetchPrincipalData}
              />
            </section>
          )} */}
        </div>
      </main>

      <CustomModal
        show={dateModalOpen}
        onHide={() => setDateModalOpen(false)}
        title="Date Range"
        width="w-[600px]"
      >
        <div className="flex flex-col items-center">
          <DateRangePicker
            ranges={dateRange}
            onChange={onRangeChange}
            maxDate={new Date()}
          />
        </div>
      </CustomModal>
    </div>
  );
};

export default AdminMIS;
